import React, { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Utils from "../../utils";
import { runbookExecutionHistoryActions } from "../../actions/runbookExecutionHistory.actions";
import { runbookExecutionHistoryService } from "../../services/runbookExecutionHistory.service";
import { runbookActions } from "../../actions/runbook.actions";
import Grid from "../common/Grid";
import ConfirmModal from "../common/ConfirmModal";
import Title from "../common/Title";
import { CiBoxList, CiClock1 } from "react-icons/ci";
import { MdError, MdCancel } from "react-icons/md";
import { Toast, ToastContainer, Spinner } from "react-bootstrap";
import Subtitle from "../common/Subtitle";
import ExecutionTriggerHelper from "../helpers/executionTriggerHelper";
import useRunbookTitle from "../../hooks/runbookTitle";
import GridHelper from "../helpers/gridHelper";
import RunbookExecutionHistoryGridFilter from "./RunbookExecutionHistoryGridFilter";
import { FaHourglassStart } from "react-icons/fa";
import { TiTick } from "react-icons/ti";
import { IoIosWarning } from "react-icons/io";
import { BsPlayFill } from "react-icons/bs";
import { RiTimerLine } from "react-icons/ri";

// When `runbookId` is passed explicitly, this instance owns its own identity, paging,
// sort, and fetched data instead of sharing the single global URL + Redux slice with every
// other mounted copy of this component. That sharing is fine when there's only ever one
// instance on screen (the standalone routed page), but a "workspace" window host (see
// WorkspaceHub) can have several Execution History windows open at once, one per runbook,
// and they must not fight over the same URL/state. The standalone route (no runbookId
// prop) keeps the original URL+Redux-driven behavior unchanged.
function RunbookExecutionHistoryGrid(props) {
  const {
    hideTitle,
    onOpenLines,
    runbookId: runbookIdProp,
    runbookGuid: runbookGuidProp,
    // Where the no-onOpenLines fallback below navigates. Defaults to the classic,
    // Layout-wrapped Lines route; the bare `/runbook/executions/popup` route (App.jsx)
    // overrides this to its own `/popup` counterpart so a drill-down inside a popped-out
    // window stays chrome-less instead of landing back on the full app shell.
    linesPath = "/runbook/executions/lines",
  } = props;
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const windowed = runbookIdProp !== undefined;

  const runbookExecutionHistoryReducer = useSelector(
    (state) => state.runbookExecutionHistory
  );
  const runbookReducer = useSelector((state) => state.runbook);
  const { runbookTitle, runbookTitleLoading } = useRunbookTitle(windowed ? runbookIdProp : undefined);

  const [confirmCancelModalShow, setConfirmCancelModalShow] = useState(false);
  const [cancelExecution, setCancelExecution] = useState({});
  const [cancelToastShow, setCancelToastShow] = useState(false);

  const filterInitialState = {
    status: [],
    startDate: "",
    endDate: "",
  };
  const [filter, setFilter] = useState(filterInitialState);

  // Windowed-only local copies of what the URL/Redux slice hold for the standalone route.
  const [localPageNumber, setLocalPageNumber] = useState(1);
  const [localOrderBy, setLocalOrderBy] = useState(undefined);
  const [localOrderDirection, setLocalOrderDirection] = useState(undefined);
  const [localResult, setLocalResult] = useState(null);
  const [localLoading, setLocalLoading] = useState(windowed);

  const fetchWindowedHistory = useCallback(() => {
    setLocalLoading(true);
    const params = {
      runbookId: runbookIdProp,
      ...(runbookGuidProp ? { runbookGuid: runbookGuidProp } : {}),
      pageNumber: localPageNumber,
      ...(localOrderBy ? { order_by: localOrderBy, order_direction: localOrderDirection } : {}),
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
    };
    Object.entries(filter).forEach(([key, value]) => {
      const hasValue = Array.isArray(value) ? value.length > 0 : !!value;
      if (hasValue) params[key] = value;
    });
    runbookExecutionHistoryService.get(params).then(
      (response) => {
        setLocalResult(response.data);
        setLocalLoading(false);
      },
      () => setLocalLoading(false)
    );
  }, [runbookIdProp, runbookGuidProp, localPageNumber, localOrderBy, localOrderDirection, filter, props.user.clientId, props.user.clientGuid]);

  const refreshGrid = () => {
    if (windowed) {
      fetchWindowedHistory();
      return;
    }
    let queryParams = Utils.getQueryStringParams(location);
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;
    dispatch(runbookExecutionHistoryActions.get(queryParams));
  };

  const handleSearch = (nextFilter) => {
    if (windowed) {
      setFilter(nextFilter);
      setLocalPageNumber(1);
      return;
    }
    GridHelper.handleSearch(nextFilter, location, navigate);
  };

  const handleCancelExecution = () => {
    setConfirmCancelModalShow(false);
    setCancelToastShow(true);
    dispatch(runbookActions.cancel({
      executionID: cancelExecution.executionId,
      cancelledbyUser: props.user.email,
      clientId: props.user.clientId,
      clientGuid: props.user.clientGuid,
      runbookId: cancelExecution.runbookId,
      runbookGuid: cancelExecution.runbookGuid || undefined,
    }));
  };

  useEffect(() => {
    if (runbookReducer.cancelResponse) {
      setTimeout(() => refreshGrid(), 2000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runbookReducer.cancelResponse]);

  const currentData = windowed
    ? localResult?.data
    : runbookExecutionHistoryReducer.paginatedResult?.data;

  const actions = [
    {
      icon: <MdCancel className="mb-1" />,
      text: "Cancel Execution",
      visibilityConditions: [{ key: "_statusRaw", operation: "===", value: "InProcess" }],
      event: (executionId) => {
        const queryParams = Utils.getQueryStringParams(location);
        const fullExecution = currentData?.find((e) => e.executionId === executionId);
        setCancelExecution({
          executionId,
          runbookId: windowed ? runbookIdProp : queryParams.runbookId || fullExecution?.runbookId,
          runbookGuid: fullExecution?.runbookGuid || (windowed ? runbookGuidProp : queryParams.runbookGuid) || null,
        });
        setConfirmCancelModalShow(true);
      },
    },
  ];

  // Standalone route: fetch is entirely URL-driven, matching the pre-existing behavior.
  useEffect(() => {
    if (windowed) return;
    let queryParams = Utils.getQueryStringParams(location);
    queryParams["clientId"] = props.user.clientId;
    queryParams["clientGuid"] = props.user.clientGuid;

    dispatch(runbookExecutionHistoryActions.get(queryParams));

    setFilter({
      // Sent/read as a single comma-separated value (see FilterBar/objectToQueryString) —
      // split back into the array the multiselect filter works with.
      status: queryParams["status"] ? queryParams["status"].split(",") : [],
      startDate: queryParams["startDate"] || "",
      endDate: queryParams["endDate"] || "",
    });
    // eslint-disable-next-line
  }, [location, windowed]);

  // Windowed: (re)fetch whenever this instance's OWN identity/paging/sort changes.
  useEffect(() => {
    if (!windowed) return;
    fetchWindowedHistory();
  }, [windowed, fetchWindowedHistory]);

  // Latest-ref so the 30s poll always calls the current refreshGrid — not one frozen at
  // whatever page/sort/identity was active when the interval was first set up.
  const refreshGridRef = useRef(refreshGrid);
  refreshGridRef.current = refreshGrid;
  useEffect(() => {
    const interval = setInterval(() => refreshGridRef.current(), 30000);
    return () => clearInterval(interval);
  }, []);

  const handleRowClick = (execution) => {
    if (windowed) {
      onOpenLines?.(execution.executionId, runbookIdProp);
      return;
    }
    const queryParams = Utils.getQueryStringParams(location);
    if (onOpenLines) {
      onOpenLines(execution.executionId, queryParams.runbookId);
    } else {
      // Navigate to the lines page with the executionId as a query parameter
      navigate(`${linesPath}?executionId=${execution.executionId}&runbookId=${queryParams.runbookId}`);
    }
  };

  let displayColumns = [
    "executionTrigger",
    "executionId",
    "startTime",
    "endTime",
    "numberOfErrors",
    "schedulerId",
    "ranByUser",
    "status",
  ];

  const data = currentData?.map(
    (eh) => {
      return {
        ...eh,
        _statusRaw: eh.status,
        startTime: Utils.convertToDateTime(eh.startTime),
        endTime: Utils.convertToDateTime(eh.endTime),
        status: (
          <div className="text-dark text-nowrap">
            {eh.status === "NotStarted" && <FaHourglassStart className="me-1 mb-1" />}
            {eh.status === "InProcess" && <BsPlayFill className="me-1 mb-1" />}
            {eh.status === "Completed" && <TiTick className="me-1 mb-1" />}
            {eh.status === "Error" && <MdError className="me-1 mb-1" />}
            {eh.status === "AuthenticationFailed" && <MdError className="me-1 mb-1" />}
            {eh.status === "CompletedWithErrors" && <IoIosWarning className="me-1 mb-1" />}
            {eh.status === "Runbook Timeout" && <RiTimerLine className="me-1 mb-1" />}
            {eh.status === "Rejected" && <MdCancel className="me-1 mb-1" />}
            {(eh.status === "Canceling" || eh.status === "Cancelling") && <MdCancel className="me-1 mb-1" />}
            {eh.status === "Cancelled" && <MdCancel className="me-1 mb-1" />}
            {Utils.fixCamelCase(eh.status)}
          </div>
        ),
        executionTrigger: ExecutionTriggerHelper.getBadge(eh.executionTrigger),
      };
    }
  );

  const loading = runbookTitleLoading || (windowed ? localLoading : runbookExecutionHistoryReducer.loading);
  const pageNumber = (windowed ? localResult?.pageNumber : runbookExecutionHistoryReducer.paginatedResult?.pageNumber) ?? 1;
  const totalPages = (windowed ? localResult?.totalPages : runbookExecutionHistoryReducer.paginatedResult?.totalPages) ?? 0;

  const handlePageChange = windowed
    ? (page) => setLocalPageNumber(page + 1)
    : GridHelper.handlePageClick;
  const handleSort = windowed
    ? (orderBy, orderDirection) => {
        setLocalOrderBy(orderBy);
        setLocalOrderDirection(orderDirection);
      }
    : GridHelper.handleSortClick;

  return (
    <>
      {!hideTitle && <Title text="Execution History" icon={<CiClock1 className="mb-1" />} />}
      {!loading && (
        <Subtitle text={runbookTitle} icon={<CiBoxList className="mb-1" />} />
      )}

      <ConfirmModal
        show={confirmCancelModalShow}
        title={`Cancel Execution`}
        question={`Are you sure you want to cancel execution ${cancelExecution.executionId}? This will stop the current run.`}
        noButtonText="No"
        yesButtonText="Yes, cancel it"
        handleClose={() => setConfirmCancelModalShow(false)}
        handleConfirm={handleCancelExecution}
      />

      <ToastContainer className="p-3" position="bottom-end" style={{ zIndex: 1 }}>
        <Toast onClose={() => setCancelToastShow(false)} show={cancelToastShow}>
          <Toast.Header>
            <strong className="me-auto">Cancel Execution</strong>
            <small>Now</small>
          </Toast.Header>
          <Toast.Body>
            {runbookReducer.loadingCancel && (
              <>
                Cancelling execution...
                <Spinner className="float-end" size="sm" variant="primary" />
                <div className="clearfix"></div>
              </>
            )}
            {!runbookReducer.loadingCancel && (
              <>
                {runbookReducer.cancelResponse && (
                  <><MdCancel className="text-secondary me-2 mb-1" />Cancellation requested successfully.</>
                )}
                {runbookReducer.cancelError && (
                  <><MdError className="text-danger me-2 mb-1" />{runbookReducer.cancelError}</>
                )}
              </>
            )}
          </Toast.Body>
        </Toast>
      </ToastContainer>

      <RunbookExecutionHistoryGridFilter
        filter={filter}
        onSearch={windowed ? handleSearch : undefined}
      />

      <Grid
        data={data}
        displayColumns={displayColumns}
        idColumn={"executionId"}
        actions={actions}
        pageNumber={pageNumber - 1}
        totalPages={totalPages}
        loading={loading}
        onPageChange={handlePageChange}
        onSort={handleSort}
        onRowClick={handleRowClick}
      ></Grid>
    </>
  );
}

export default RunbookExecutionHistoryGrid;
